import React from 'react';
import { motion } from 'framer-motion';
import { Bookmark, FileText } from 'lucide-react';
import { MathText, accent, difficultyTone, springs } from '../lib/utils-app';

const LETTERS = ['A', 'B', 'C', 'D'];

function QuestionMeta({ question, sectionName, sectionAccent }) {
  const a = accent(sectionAccent || 'indigo');
  const tags = [sectionName, question.topic, question.questionType].filter(Boolean);
  return (
    <div className="flex flex-wrap items-center gap-2 mb-5">
      <span className={`inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em] font-medium px-2 py-1 rounded-md ${a.soft} ${a.text} ring-1 ${a.ring}`}>
        <span className={`w-1 h-1 rounded-full ${a.dot}`} />
        {tags[0]}
      </span>
      {tags.slice(1).map((t) => (
        <span key={t} className="text-[10.5px] uppercase tracking-[0.16em] font-medium px-2 py-1 rounded-md bg-white/[0.03] text-slate-300 ring-1 ring-white/5">
          {t}
        </span>
      ))}
      {question.difficulty && (
        <span className={`ml-1 text-[10.5px] uppercase tracking-[0.16em] font-medium px-2 py-1 rounded-md ring-1 ${difficultyTone(question.difficulty)}`}>
          {question.difficulty}
        </span>
      )}
    </div>
  );
}

function BookmarkButton({ active, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label="Bookmark question"
      className={`p-2 rounded-lg ring-1 transition ${active ? 'bg-amber-400/10 text-amber-300 ring-amber-400/30' : 'bg-white/[0.03] text-slate-400 ring-white/5 hover:text-slate-200'}`}
    >
      <Bookmark className="w-4 h-4" fill={active ? 'currentColor' : 'none'} />
    </button>
  );
}

function OptionButton({ letter, text, state, disabled, onClick }) {
  const styles = {
    idle:     { wrap: 'border-white/8 bg-white/[0.02] hover:bg-white/[0.04]', chip: 'bg-white/[0.06] text-slate-300' },
    selected: { wrap: 'border-indigo-400/40 bg-indigo-400/[0.06]',           chip: 'bg-indigo-400 text-slate-950' },
    correct:  { wrap: 'border-emerald-400/40 bg-emerald-400/[0.06]',         chip: 'bg-emerald-400 text-slate-950' },
    wrong:    { wrap: 'border-rose-400/40 bg-rose-400/[0.06]',               chip: 'bg-rose-400 text-slate-950' },
  }[state];
  return (
    <motion.button
      whileHover={disabled ? undefined : { y: -1 }}
      whileTap={disabled ? undefined : { scale: 0.985 }}
      transition={springs.snappy}
      onClick={onClick}
      disabled={disabled}
      className={`text-left rounded-xl border ${styles.wrap} px-4 py-3.5 flex items-start gap-3 transition-colors`}
    >
      <div className={`shrink-0 mt-0.5 w-7 h-7 rounded-lg text-[12px] font-semibold flex items-center justify-center ${styles.chip}`}>
        {letter}
      </div>
      <div className="flex-1 text-[15px] text-slate-100 font-serif-q leading-relaxed"><MathText text={text} /></div>
    </motion.button>
  );
}

export default function QuestionCard({
  question, sectionName, sectionAccent, selected, revealed,
  isBookmarked, onPick, onToggleBookmark, index, total,
}) {
  const stateFor = (letter) => {
    if (!revealed) return selected === letter ? 'selected' : 'idle';
    if (question.correctAnswer && letter === question.correctAnswer.toUpperCase().trim()) return 'correct';
    if (selected === letter) return 'wrong';
    return 'idle';
  };
  const hasOptions = question.hasOptions && (question.options || []).length > 0;

  return (
    <article className="relative rounded-2xl glass hairline p-7 md:p-9">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-medium truncate">
          § {sectionName} · {question.topic}
        </div>
        <div className="flex items-center gap-2">
          <BookmarkButton active={isBookmarked} onClick={onToggleBookmark} />
          <span className="text-[11px] font-mono text-slate-500 tabular-nums">{String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
        </div>
      </div>

      <QuestionMeta question={question} sectionName={sectionName} sectionAccent={sectionAccent} />

      <div className="font-serif-q text-[18px] md:text-[19px] leading-[1.7] text-slate-50 tracking-[-0.005em]">
        {question.questionNo && <span className="font-medium text-slate-300 mr-1">Q{question.questionNo}.</span>}
        <MathText text={question.question} />
      </div>

      {hasOptions ? (
        <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-3">
          {question.options.map((opt) => (
            <OptionButton
              key={`${question.id}-opt-${opt.letter}`}
              letter={opt.letter}
              text={opt.text}
              state={stateFor(opt.letter)}
              disabled={revealed}
              onClick={() => onPick(opt.letter)}
            />
          ))}
        </div>
      ) : (
        <div className="mt-7 rounded-xl border border-dashed border-white/10 bg-white/[0.02] px-4 py-3.5 text-[12.5px] text-slate-400 flex items-center gap-2">
          <FileText className="w-4 h-4 text-slate-500" />
          Subjective / proof problem — work it out in the Scratchpad, then reveal the solution.
        </div>
      )}

      <div className="mt-7 pt-4 border-t border-white/5 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
        <div className="flex items-center gap-2 tracking-wide">
          <span className="font-mono text-slate-600">cite</span>
          <span className="italic font-serif-q">{question.source}</span>
        </div>
        <div className="flex items-center gap-3 uppercase tracking-[0.16em]">
          {question.year && <span>{question.year}</span>}
          <span className="w-px h-3 bg-white/10" />
          <span>id · <span className="font-mono normal-case tracking-normal text-slate-400">{question.id.slice(0, 8)}</span></span>
        </div>
      </div>
    </article>
  );
}

export { LETTERS };
